import { Component, HostBinding, QueryList, ViewChild, ViewChildren, ViewEncapsulation, OnInit } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ICellRendererParams, SelectionChangedEvent, RowNode } from 'ag-grid-community';
import * as _ from 'underscore';
import { Endpoint, HelpIndex, LoadFileType, MessageType, TransformerType } from '../../../../models/enums';
import { FieldMapping } from '../../../../models/field-mapping';
import { CellActionType, GenericColDef, GridRowSelectionType, FilterTemplateType, GridContext } from '../../../../models/grid-context';
import { Selectable } from '../../../../models/selectable';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DragCellComponent } from '../../../grid-templates/drag-cell/drag-cell.component';
import { TransformersModalComponent } from '../../transformers-modal/transformers-modal.component';
import { DrRequestInfo } from '../../../../models/dr-request-info';
import { LoadFileDiscoverFieldsModalComponent } from '../load-file-import-discover-fields-modal/load-file-import-discover-fields-modal.component';
import { isNullOrUndefined } from '../../../../models/utils';
import { ObjectActionListType, Permissions, ActionType } from '../../../../models/permission';
import { clone } from 'lodash';
import { TemplateType } from '../../../../models/template';

@Component({
    standalone: false,
    selector: 'res-load-file-import',
    templateUrl: './load-file-import.component.html',
    styleUrls: ['./load-file-import.component.scss'],
    encapsulation: ViewEncapsulation.None
})

export class LoadFileImportComponent implements SettingsNavigationContentComponent {
    readonly FieldMapping = FieldMapping;
    @HostBinding('class') classes = 'maximized-component';
    @ViewChildren(GenericGridComponent) genericGrids: QueryList<GenericGridComponent>;
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;

    isBusy: boolean;
    isBusyMessage: string;
    validChanges = true;
    isDirty = false;

    helpIndex: HelpIndex;
    handle: string;
    isProject: boolean;
    automationId = 'load-file-import';
    loadFileFields = new Array<Selectable>();
    originalLoadFileFields: Array<string>;
    loadFiledFieldsChanged: boolean;
    loadFileFieldNamesHasChanges: boolean;
    currentlyChangingLoadFileFieldName: boolean;
    originalFieldTransformerTypeChangeMap: Map<string, string>; // digitalReefMetadataValue, original type?

    mappings = new Array<FieldMapping>();
    originalMappings: Array<FieldMapping>;
    metadataFields = new Array<any>();
    numSelectedMappings = 0;
    selectAllChecked = false;
    preserveFamily = true;
    originalPreserveFamily = true;
    loadImages: boolean;
    originalLoadImages: boolean;
    leftListSize: number;
    numConcurrentRequests = 0;
    profileRequest: any;
    tmpIconClass: string;
    hasPermissions: boolean;
    transformerList = [
        { label: 'None', icon: 'fa-long-arrow-right', colorClass: 'color-grey-07', type: TransformerType.none },
        { label: 'Date / Time ...', icon: 'fa-calendar', colorClass: 'color-blue-01', type: TransformerType.date },
        { label: 'Number ...', icon: 'fa-hashtag', colorClass: 'color-blue-01', type: TransformerType.number },
        { label: 'Text ...', icon: 'fa-font', colorClass: 'color-blue-01', type: TransformerType.text },
    ];

    transformerIsDirty: boolean;
    loadFileFieldsSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    loadFileFieldsColumns = new Array<GenericColDef>(
        {
            colId: 'checkbox',
            automationId: this.automationId + '-field-name',
            minWidth: 50, maxWidth: 50, width: 50,
            headerCheckboxSelection: true,
            cellRenderer: DragCellComponent,
            lockVisible: true,
            colSpan: () => 2, // a single cell pretending to be an entire row
            field: 'externalName',
            headerName: '',
            cellRendererParams: {
                checkable: true,
                inlineEdit: () => true,
                onInlineEdit: this.onFieldNameChange.bind(this),
                required: () => false,
                setDraggedItem: (param) => this.leftDraggedItem = param,
                onDragCanceled: () => this.leftDraggedItem = null
            }
        },
        {
            headerName: 'Field Name',
            field: 'externalName',
            automationId: this.automationId,
            lockVisible: true,
            filterType: FilterTemplateType.freeform,
            suppressMovable: true,
        }
    );

    isXmlFileLoaded = false;
    numSelectedFileFields = 0;
    draggedItem = null;
    leftDraggedItem = null;

    metadataSelection = GridRowSelectionType.single;
    metadataColumns = new Array<GenericColDef>(
        {
            headerName: 'Field Name',
            field: 'name',
            lockVisible: true,
            automationId: this.automationId + '-dr-metadata-fields',
            cellRenderer: DragCellComponent,
            cellRendererParams: {
                dragScope: ['metadata'],
                inlineEdit: () => false,
                required: this.required.bind(this),
                setDraggedItem: (param) => this.draggedItem = param,
                onDragCanceled: () => this.draggedItem = null
            },
            filterType: FilterTemplateType.freeform,
            suppressMovable: true
        }
    );

    gridContext = new GridContext({
        onFilterApply: (queryStr: string) => this.onFilterApply(queryStr),
        inlineGridFilter: true
    });

    gridContextLoadFile = new GridContext({
        inlineGridFilter: true
    });

    metadataRequestInfo = new DrRequestInfo({
        endpoint: Endpoint.realmManager_getAllFieldsDocumentMetadataTemplate,
        data: {
            containerId: ''
        },
        responseName: 'metadataFolders'
    });

    latestLoadFileClick: any;
    latestFiledMappingSelected: any;

    rowDisableRulesLoadFields = {
        'disabled-row': () => {
            return !this.hasPermissions;
        },
    };

    loadFileFieldsCellClickedCallback(params, action: CellActionType, event: MouseEvent) {
        params.node.setSelected(!params.node.isSelected());
        if (event.shiftKey && this.latestLoadFileClick && params.node.isSelected()) {
            this.selectAllNodesBetween(this.genericGrids.first, this.latestLoadFileClick, params.node);
        }
        this.latestLoadFileClick = params.node;
    }

    selectAllNodesBetween(grid: GenericGridComponent, first: RowNode, second: RowNode): void {
        const rows = grid.getRows();
        let active = false;
        rows.forEach((row) => {
            if (active) {
                row.setSelected(true);
            }
            if (row.data === first.data || row.data === second.data) {
                active = !active;
            }
        });
    }

    onFieldNameChange(): void {
        this.currentlyChangingLoadFileFieldName = true;
    }

    required(cellData: any, preserveFamily = this.preserveFamily): boolean {
        if (this.isXmlFileLoaded) {
            return false;
        } else {
            return cellData.name === 'extbegdoc' || (preserveFamily && cellData.name === 'extbegattach');
        }
    }

    rowHeightCallback = (params: any) => {
        return 50;
    }

    isLocked = (mapped: FieldMapping): boolean => {
        return !this.isXmlFileLoaded && ((mapped.digitalReefMetadataValue === 'extbegdoc') ||
            (mapped.digitalReefMetadataValue === 'extbegattach' && this.preserveFamily));
    }

    isFullySelected = (): boolean => {
        const numSelected = _.filter(this.mappings, (thisMapping) => thisMapping.isSelected && !this.isLocked(thisMapping)).length;
        const numUnselected = _.filter(this.mappings, (thisMapping) => !thisMapping.isSelected && !this.isLocked(thisMapping)).length;
        return numSelected > 0 && numUnselected === 0;
    }

    isPartiallySelected = (): boolean => {
        const currentlySelected = _.filter(this.mappings, (thisMapping) => thisMapping.isSelected).length;
        const notLocked = _.filter(this.mappings, (thisMapping) => !this.isLocked(thisMapping)).length;
        if (currentlySelected === 0) {
            this.selectAllChecked = false;
            return undefined;
        } else if (currentlySelected === notLocked) {
            this.selectAllChecked = true;
            return false;
        }
        return currentlySelected > 0 && currentlySelected < notLocked;
    }

    preserveToggled = (): void => {
        this.genericGrids.last.setData(this.metadataFields);
        if (this.genericGrids && this.genericGrids.last.gridContext) {
            this.genericGrids.last.gridContext.clearAllFilters.emit();
            this.genericGrids.last.loadData();
        }
    }

    constructor(private commonService: CommonService, private restService: RestService) { }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string): void {
        if (node && node.data) {
            this.handle = projectHandle ? node.data.id : node.data.handle;
            this.metadataRequestInfo.data.containerId = this.handle;
        }
        this.isProject = projectHandle !== null;
        const permissionType = this.isProject ? ObjectActionListType.PRJ_LOADFILE_SETTINGS : ObjectActionListType.ORG_LOADFILE_SETTINGS;
        this.hasPermissions = Permissions.checkUserPermission(permissionType, ActionType.CREATE);
        if (!this.hasPermissions && this.loadFileFieldsColumns) {
            // remove from grids checkbox column
            const checkBoxCol = this.loadFileFieldsColumns.find(col => col.colId === 'checkbox');
            if (checkBoxCol) {
                checkBoxCol.headerCheckboxSelection = false;
                checkBoxCol.cellRendererParams.checkable = false;
                checkBoxCol.cellRendererParams.inputDisabled = true;
                checkBoxCol.cellRendererParams.dragScope = null;
                checkBoxCol.field = '';
            }
        }
        this.helpIndex = HelpIndex.org_load_file_import_settings_template;
        this.loadData();
    }

    selectionChanged(event, mapping?: FieldMapping): void {
        if (!isNullOrUndefined(mapping)) {
            if (event.shiftKey && this.latestFiledMappingSelected && this.latestFiledMappingSelected != mapping) {
                const lastIndex = this.mappings.findIndex(m => m === this.latestFiledMappingSelected);
                const currentIndex = this.mappings.findIndex(m => m === mapping);
                const minIndex = Math.min(lastIndex, currentIndex), maxIndex = Math.max(lastIndex, currentIndex);
                for (let i = minIndex; i <= maxIndex; i++) {
                    if (!this.isLocked(this.mappings[i])) {
                        this.mappings[i].isSelected = mapping === this.mappings[i] ? !mapping.isSelected : true;
                        this.numSelectedMappings = this.mappings[i].isSelected ? this.numSelectedMappings + 1 : this.numSelectedMappings > 0 ? this.numSelectedMappings - 1 : 0;
                    }
                }
            } else {
                if (!this.isLocked(mapping)) {
                    mapping.isSelected = !mapping.isSelected;
                    this.numSelectedMappings = mapping.isSelected ? this.numSelectedMappings + 1 : this.numSelectedMappings > 0 ? this.numSelectedMappings - 1 : 0;
                }
            }
        } else {
            const currentlySelectedOrLocked = _.filter(this.mappings, (thisMapping) => thisMapping.isSelected || this.isLocked(thisMapping)).length;
            if (currentlySelectedOrLocked === this.mappings.length) {
                this.numSelectedMappings = 0;
                _.each(this.mappings, (thisMapping) => { thisMapping.isSelected = false; });
                this.selectAllChecked = false;
            } else {
                let selectedCount = 0;
                _.each(this.mappings, (thisMapping) => {
                    if (!this.isLocked(thisMapping)) {
                        thisMapping.isSelected = true;
                        selectedCount++;
                    }
                });
                this.numSelectedMappings = selectedCount;
                this.selectAllChecked = true;
            }
        }
        this.latestFiledMappingSelected = mapping;
    }

    initializeDirtyStateVariables() {
        this.isDirty = false;
        this.loadFiledFieldsChanged = false;
        this.loadFileFieldNamesHasChanges = false;
        this.currentlyChangingLoadFileFieldName = false;
        this.transformerIsDirty = false;
    }

    loadData(): void {
        this.initializeDirtyStateVariables();
        this.isBusyMessage = 'Loading Fields and Mappings...';
        this.validChanges = true;
        this.loadFileFieldsData();
        if (this.genericGrids && this.genericGrids.last && this.genericGrids.last.gridContext && this.genericGrids.first && this.genericGrids.first.gridContext) {
            this.genericGrids.last.gridContext.clearAllFilters.emit();
            this.genericGrids.first.gridContext.clearAllFilters.emit();
            this.genericGrids.last.loadData();
        }
        this.numSelectedFileFields = 0;
        this.numSelectedMappings = 0;
    }

    loadFileFieldsData(): void {
        this.numConcurrentRequests++;
        this.loadFileFields = new Array<Selectable>();
        this.mappings = new Array<FieldMapping>();
        this.originalMappings = new Array<FieldMapping>();
        this.originalLoadFileFields = new Array<string>();
        this.isBusyMessage = 'Loading Fields and Mappings...';
        this.isBusy = true;
        this.restService.post(Endpoint.projectManager_getLoadFileMetadataProfile, { handle: this.handle, requestHandle: this.handle }).then(responseAllFields => {
            if (responseAllFields && responseAllFields.requestHandle === this.handle && responseAllFields.profile) {
                this.profileRequest = responseAllFields.profile;
                this.preserveFamily = this.originalPreserveFamily = this.profileRequest.preserveFamily;
                this.loadImages = this.originalLoadImages = this.profileRequest.loadImages;
                this.isXmlFileLoaded = this.profileRequest.loadFileType === LoadFileType.EDRM_XML;
                if (responseAllFields.profile.availableLoadFileFields) {
                    responseAllFields.profile.availableLoadFileFields = responseAllFields.profile.availableLoadFileFields.sort();
                    responseAllFields.profile.availableLoadFileFields.forEach(element => {
                        this.loadFileFields.push(new Selectable({
                            externalName: element
                        }));
                        this.originalLoadFileFields.push(element);
                    });
                    this.genericGrids.first.setData(this.loadFileFields);
                }
                if (responseAllFields.profile.entries) {
                    responseAllFields.profile.entries.forEach(element => {
                        this.mappings.push(new FieldMapping(element));
                        this.originalMappings.push(new FieldMapping(element));
                    });
                }
            }
            this.numConcurrentRequests--;
            this.isBusy = this.numConcurrentRequests > 0;
        }, error => {
            this.numConcurrentRequests--;
            this.isBusy = this.numConcurrentRequests > 0;
        });
    }

    onCustomFilterCallback = (event?: SelectionChangedEvent) => {
        this.genericGrids.first.setData(this.loadFileFields);
        if (this.genericGrids.first.gridCheckedItems.length > 0) {
            this.genericGrids.first.gridApi.forEachNode((node) => {
                this.genericGrids.first.gridCheckedItems.forEach((entry) => {
                    if (_.isEqual(node.data, entry)) {
                        node.setSelected(true);
                    }
                });
            });
        }
    }

    organizeMetadata(preserveFamily?: boolean, sortFn?: (a: any, b: any) => number): void {
        this.metadataFields.sort(sortFn);
        const requiredFields = this.metadataFields.filter(field => this.required(field, preserveFamily));
        this.metadataFields.unshift(...requiredFields);
        this.metadataFields = _.uniq(this.metadataFields);
    }

    metadataResponseCallback = (responseAllFields: any): void => {
        this.metadataFields = new Array<any>();
        if (responseAllFields && responseAllFields.metadataFolders) {
            _.each(responseAllFields.metadataFolders, (element) => {
                this.metadataFields = this.metadataFields.concat(element.fields);
            });
            _.each(this.metadataFields, (element, index, list) => {
                list[index] = { name: element };
            });
            this.organizeMetadata(this.preserveFamily, this.compare);
            this.metadataFields = _.filter(this.metadataFields, (field) => !_.some(this.mappings, (mapping) => mapping.digitalReefMetadataValue === field.name));
            responseAllFields.metadataFolders = this.metadataFields;
        }
    }

    onFilterApply(queryStr: string): void {
        const filtered = _.filter(this.metadataFields, (field) => field.name ? field.name.toLowerCase().indexOf(queryStr.toLowerCase()) !== -1 : false);
        this.genericGrids.last.setData(filtered);
    }

    countSelectedLoadFileFields = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.numSelectedFileFields = event.api.getSelectedRows().length;
        }
    }

    deleteSelectedFileFields(): void {
        if (this.genericGrids?.first?.gridApi) {
            const selectedLoadFileFields = this.genericGrids.first.gridApi.getSelectedRows();
            this.loadFileFields = _.filter(this.loadFileFields, (field) =>
                _.find(selectedLoadFileFields, (selectedField) => field.externalName === selectedField.externalName) === undefined);
            this.genericGrids.first.setData(this.loadFileFields);
            this.numSelectedFileFields = 0;
        }
    }

    countSelectedMappings = (event?: SelectionChangedEvent) => {
        if (event && event.api) {
            this.numSelectedFileFields = event.api.getSelectedRows().length;
        }
    }

    deleteSelectedMappings(): void {
        const removedMetadataFields = _.filter(_.map(this.mappings, (mapping) => mapping.isSelected && mapping.digitalReefMetadataValue ?
            { name: mapping.digitalReefMetadataValue } : null), item => item);
        this.metadataFields.push(...removedMetadataFields);
        this.metadataFields.sort(this.compare);
        this.organizeMetadata(this.preserveFamily, this.compare);
        this.genericGrids.last.setData(this.metadataFields);
        this.mappings = _.filter(this.mappings, (mapping) => !mapping.isSelected);
        this.numSelectedMappings = 0;
    }

    // For future use.  Do not delete
    private getNewFieldName(): string {
        let index = 1;
        const prefix = 'userField';
        while (_.find(this.loadFileFields, (item: Selectable) => prefix + index === item.externalName)) {
            index++;
        }
        return prefix + index;
    }

    // For future use.  Do not delete
    addFileField(): void {
        this.isDirty = true;
        this.loadFileFields.push(new Selectable({
            externalName: this.getNewFieldName(),
            userField: true
        }));

        this.genericGrids.first.setData(this.loadFileFields);
        this.genericGrids.first.gridApi.setFocusedCell(this.loadFileFields.length - 1, 'checkbox');
        if (!isNullOrUndefined(document.activeElement) && !isNullOrUndefined(document.activeElement.children[0].children[0])) {
            (document.activeElement.children[0].children[0].children[2] as HTMLInputElement).select();
        }
    }

    isDropAllowed = (dragData: any) => {
        return (dragData instanceof Selectable) && (this.numSelectedFileFields < 2);
    }

    onFieldDrop(event: any, index?: number): void {
        if (event?.data && event.data.externalName) {
            if (index !== undefined && index > -1) {
                ((this.mappings[index].externalName && (this.mappings[index].externalName.indexOf(';') !== -1)) ?
                    this.commonService.openConfirmationModal('Replace the existing Load File Fields with ' + event.data.externalName +
                        ' field? The current transformer will be reset.', 'Load File Import', 'OK', 'Cancel').then(response => {
                            return response.modalResult;
                        }) : Promise.resolve(true)).then(replaceField => {
                            if (replaceField) {
                                this.mappings[index].externalName = event.data.externalName;
                                this.mappings[index].userField = false;
                                this.mappings[index].fieldTransformerType = TransformerType.none;
                                this.mappings[index].compositeFields = null;
                                this.mappings[index].transformerAttributes = null;
                            } else {
                                return;
                            }
                        });
            } else {
                const selectedRows = this.numSelectedFileFields < 2 ? [event.data] : this.genericGrids.first.gridApi.getSelectedRows();
                _.each(selectedRows, (row) => {
                    this.mappings.push(new FieldMapping({
                        digitalReefMetadataValue: '',
                        externalName: row.externalName,
                        fieldTransformerType: null
                    }));
                });
            }
            this.leftDraggedItem = null;
        } else {
            this.mappings.push(new FieldMapping({
                digitalReefMetadataValue: event.data.name,
                fieldTransformerType: null
            }));
            this.metadataFields.splice(this.metadataFields.findIndex(m => m.name === event.data.name), 1);
            const metadataArray = this.genericGrids.last.getData();
            metadataArray.splice(metadataArray.findIndex(ma => ma.name === event.data.name), 1);
            this.genericGrids.last.setData(metadataArray);
            this.draggedItem = null;
        }
    }

    onMetadataFieldDrop(event: any, index: number) {
        if (event?.data && !this.isLocked(this.mappings[index])) {
            const kickedField = this.mappings[index].digitalReefMetadataValue;
            this.mappings[index].digitalReefMetadataValue = event.data.name;
            if (!this.mappings[index].fieldTransformerType) {
                this.mappings[index].fieldTransformerType = TransformerType.none;
            }
            if (kickedField && kickedField.length > 0) {
                this.metadataFields.push({ name: kickedField });
                this.organizeMetadata(this.preserveFamily, this.compare);
            }
            this.metadataFields.splice(this.metadataFields.findIndex(m => m.name === event.data.name), 1);
            const metadataArray = this.genericGrids.last.getData();
            metadataArray.splice(metadataArray.findIndex(ma => ma.name === event.data.name), 1);
            this.genericGrids.last.setData(metadataArray);
        }
        this.draggedItem = null;
    }

    private scrubProfile() {
        if (this.profileRequest) {
            this.profileRequest.availableLoadFileFields = _.map(this.loadFileFields, field => field.externalName);
            this.mappings.forEach(m => {
                if (m.fieldTransformerType === TransformerType.number) {
                    // need this replacement since number transformer type is used only internaly by UI
                    m.fieldTransformerType = TransformerType.text;
                }
            });
            this.profileRequest.entries = [];
            this.mappings.forEach((value) => {
                this.profileRequest.entries.push({
                    composite: value.composite,
                    compositeFields: value.compositeFields,
                    digitalReefMetadataValue: value.digitalReefMetadataValue,
                    externalName: value.externalName,
                    fieldTransformerType: value.fieldTransformerType,
                    transformerAttributes: value.transformerAttributes
                });
            });

            const propsToRemove = ['iconColor', 'iconClass', '_fieldTransformerType'];
            _.each(propsToRemove, propertyName => { delete this.profileRequest.entries[propertyName]; });
            delete this.profileRequest['id'];
            this.profileRequest['profileHandle'] = this.profileRequest['projectId'];
            delete this.profileRequest['projectId'];
            const innerDeleteList = ['hasCheckbox', 'userField', 'isSelected', 'iconClass', 'iconColor'];
            _.each(innerDeleteList, propertyName => {
                _.each(this.profileRequest.entries, entry => { delete entry[propertyName]; });
            });
            if (this.isProject) {
                this.profileRequest.profileHandle = this.handle;
            }
        }
    }

    saveChanges(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.validChanges = false;
            if (this.handle && this.profileRequest) {
                const mappedFields = _.map(this.mappings, (mapping) => mapping.digitalReefMetadataValue);
                if (_.some(this.mappings, (mapping) =>
                (!mapping.digitalReefMetadataValue || mapping.digitalReefMetadataValue.length < 1 ||
                    !mapping.externalName || mapping.externalName.length < 1)
                )) {
                    this.commonService.openErrorModal('Please complete both sides of all mappings.', 'Incomplete mappings exist');
                    resolve(false);
                    return;
                }

                this.profileRequest.preserveFamily = this.preserveFamily;
                this.profileRequest.loadImages = this.loadImages;
                let errorString = 'Please create the required mapping for the extbegdoc DR field.';
                if (!this.isXmlFileLoaded && !_.find(this.mappings, (mapping) => mapping.digitalReefMetadataValue === 'extbegdoc')) {
                    this.commonService.openErrorModal(errorString, 'Load File Import');
                    resolve(false);
                    return;
                }
                if (this.preserveFamily) {
                    errorString = 'The "Preserve Families" option is enabled. ' +
                        'The required mapping to preserve families is missing.' +
                        ' Please create a mapping for the extbegattach DR field.';
                    if (_.intersection(['extbegattach'], mappedFields).length !== 1) {
                        this.commonService.openErrorModal(errorString, 'Load File Import');
                        resolve(false);
                        return;
                    }
                }

                this.validChanges = true;
                this.originalFieldTransformerTypeChangeMap = null;
                this.transformerIsDirty = false;
                resolve(this.saveChangesPost(fromAutoSave));
            }
        });
    }

    saveChangesPost(fromAutoSave = false): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.isBusyMessage = 'Saving Fields and Mappings...';
            this.isBusy = true;
            this.numConcurrentRequests++;
            this.scrubProfile();

            this.restService.post(Endpoint.projectManager_setLoadFileMetadataProfile,
                this.profileRequest).then(response => {
                    this.numConcurrentRequests--;
                    this.isDirty = false;
                    if (!fromAutoSave) {
                        this.loadData();
                    }
                    resolve(true);
                }, error => {
                    this.numConcurrentRequests--;
                    this.isBusy = this.numConcurrentRequests > 0;
                    resolve(false);

                });

        });
    }

    discardChanges(): void {
        this.isDirty = false;
        this.preserveFamily = this.originalPreserveFamily = true;
        this.loadImages = this.originalLoadImages = false;
        this.numSelectedFileFields = 0;
        this.numSelectedMappings = 0;
        this.originalFieldTransformerTypeChangeMap = null;
        this.loadData();
        this.genericGrids.first.refreshColumns();
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    private compare(a: any, b: any) {
        if (a.name < b.name) {
            return -1;
        } else if (a.name > b.name) {
            return 1;
        }
        return 0;
    }

    showTransformerMenu(event: MouseEvent, index: number): void {
        if (this.hasPermissions && this.mappings[index].externalName && this.mappings[index].digitalReefMetadataValue) {
            const button = event.target as HTMLButtonElement;
            const image = button.children[0] as HTMLImageElement;
            button.classList.add('transform-menu');
            if (image) {
                image.classList.remove(this.mappings[index].iconClass, this.mappings[index].iconColor);
                image.classList.add('fa-caret-down', 'color-blue-01');
            }
        }
    }

    hideTransformerMenu(event: MouseEvent, index: number): void {
        const button = event.target as HTMLButtonElement;
        const image = button.children[0] as HTMLImageElement;
        button.classList.remove('transform-menu');
        if (image) {
            image.classList.remove('fa-caret-down', 'color-blue-01');
            image.classList.add(this.mappings[index].iconClass, this.mappings[index].iconColor);
        }
    }

    openTransformerModal(item: FieldMapping, selectedType: TransformerType): void {
        if (item && selectedType) {
            if (isNullOrUndefined(this.originalFieldTransformerTypeChangeMap)) {
                this.originalFieldTransformerTypeChangeMap = new Map<string, string>();
            }

            if ((item.fieldTransformerType !== selectedType)) {
                if (this.originalFieldTransformerTypeChangeMap.has(item.digitalReefMetadataValue) === false) {
                    this.originalFieldTransformerTypeChangeMap.set(item.digitalReefMetadataValue, item.fieldTransformerType);
                } else {
                    const originalType = this.originalFieldTransformerTypeChangeMap.get(item.digitalReefMetadataValue);
                    if (originalType === selectedType) {
                        this.originalFieldTransformerTypeChangeMap.delete(item.digitalReefMetadataValue);
                    }
                }
            }

            if (selectedType !== TransformerType.none) {
                const clonedItem = clone(item);
                clonedItem.fieldTransformerType = selectedType;
                this.commonService.openModal(TransformersModalComponent, { modalData: { fields: this.loadFileFields, selectedLoadFileField: clonedItem } }).then((response) => {
                    if (response && response.modalResult && response.modalData) {
                        if (response.modalData.transformerAttribs) {
                            item.transformerAttributes = response.modalData.transformerAttribs;
                        }
                        if (response.modalData.compositeFields) {
                            item.composite = true;
                            item.compositeFields = response.modalData.compositeFields;
                        }
                        item.fieldTransformerType = response.modalData.fieldTransformerType;
                        if (response.modalData.selectedField && response.modalData.selectedField !== item.externalName) {
                            item.externalName = response.modalData.selectedField;
                        }

                        if (this.originalFieldTransformerTypeChangeMap.has(item.digitalReefMetadataValue) === false) {
                            this.originalFieldTransformerTypeChangeMap.set(item.digitalReefMetadataValue, selectedType);
                        }
                        this.transformerIsDirty = true;
                    }
                });
            } else {
                item.transformerAttributes = null;
                item.compositeFields = null;
                item.composite = false;
                item.fieldTransformerType = selectedType;
                this.transformerIsDirty = true;
                // if there are 2 fields separated by ; remove the second one
                if (item.externalName && item.externalName.includes(';')) {
                    item.externalName = item.externalName.split(';')[0];
                }
            }
        }
    }

    openDiscoverFieldsModal(): void {
        ((this.loadFileFields && this.loadFileFields.length > 0) ?
            this.commonService.openConfirmationModal(
                'All existing Load File Fields, including user-created fields, will be replaced.\nAll existing field mappings will be maintained.',
                'Discover and Replace Load File Fields', 'Continue', 'Cancel').then(response => {
                    return response.modalResult;
                }) : Promise.resolve(true)).then(launchModal => {
                    if (launchModal) {
                        this.commonService.openModal(LoadFileDiscoverFieldsModalComponent, { modalData: this.profileRequest, class: 'vertical-percent-100 horizontal-percent-100' }).then((result) => {
                            if (result.modalResult && result.modalData) {
                                // Note: Do not sort returned fields.  The service-tier returns the fields sorted, as expected.
                                // They are arranged alphabetically with mixed cases - example: ape Apple art Bob bob
                                this.loadFileFields = result.modalData.fields;
                                this.profileRequest.specialCharacters = result.modalData.profile.specialCharacters;
                                this.profileRequest.encodingType = result.modalData.profile.encodingType;
                                this.genericGrids.first.setData(this.loadFileFields);
                                this.loadFiledFieldsChanged = true;
                                this.isXmlFileLoaded = result.modalData.isXml;
                                if (this.isXmlFileLoaded) {
                                    this.preserveFamily = false;
                                    this.profileRequest.loadFileType = LoadFileType.EDRM_XML;
                                } else {
                                    this.preserveFamily = true;
                                }
                                this.currentlyChangingLoadFileFieldName = false;
                            }
                        });
                    }
                });
    }

    isTransformerDisabled(selectedTransformer: TransformerType, type: TransformerType): boolean {
        if (selectedTransformer === TransformerType.none) {
            return type === TransformerType.none; // disable none transformer
        } else if (selectedTransformer !== type && type !== TransformerType.none) {
            return true; // disable all except none and current selected transformer
        }
    }

    isDisabled(): boolean {
        this.isDirty = this.dirty();
        this.validate();
        return (this.isBusy || !this.isDirty);
    }

    loadFileFieldNamesDirty(): void {
        let dirty = false;
        const rows = this.genericGrids.first.getRows();
        if (!isNullOrUndefined(rows) && (rows.length > 0)) {
            for (let i = 0; i < this.originalLoadFileFields.length; i++) {
                if (this.originalLoadFileFields[i] !== rows[i].data.externalName.trim()) {
                    dirty = true;
                    this.loadFileFieldNamesHasChanges = true;
                    break;
                }
            }
        }

        // don't set this flag until we have a change occuring and we are sure no fields are dirty.
        if (!dirty) {
            this.loadFileFieldNamesHasChanges = false;
        }

        this.currentlyChangingLoadFileFieldName = false;

        return;
    }

    dirty(): boolean {
        if (this.handle && this.profileRequest) {
            if (this.loadFiledFieldsChanged) {
                return true;
            }

            if ((this.preserveFamily !== this.originalPreserveFamily) || (this.loadImages !== this.originalLoadImages)) {
                return true;
            }

            if (!isNullOrUndefined(this.mappings) && !isNullOrUndefined(this.originalMappings)) {
                if (this.originalMappings.length !== this.mappings.length) {
                    return true;
                } else if (this.mappings.length > 0) {
                    for (let i = 0; i < this.mappings.length; i++) {
                        if (this.mappings[i].digitalReefMetadataValue !== this.originalMappings[i].digitalReefMetadataValue ||
                            this.mappings[i].externalName !== this.originalMappings[i].externalName) {
                            return true;
                        }
                    }
                }

                if (this.originalLoadFileFields.length !== this.loadFileFields.length) {
                    return true;
                }
            }

            if (this.currentlyChangingLoadFileFieldName) {
                this.loadFileFieldNamesDirty();
            }

            // this flag is set implicity in this.loadFileFieldNamesDirty();
            if (this.loadFileFieldNamesHasChanges) {
                return true;
            }

            return this.transformerIsDirty;

        }
        return false;
    }


    validate(): boolean {
        this.validChanges = true;
        if (this.handle && this.profileRequest) {
            if (this.validChanges && this.mappings && (this.mappings.length > 0)) {
                const mappedFields = _.map(this.mappings, (mapping) => mapping.digitalReefMetadataValue);
                if (_.some(this.mappings, (mapping) =>
                (!mapping.digitalReefMetadataValue || mapping.digitalReefMetadataValue.length < 1 ||
                    !mapping.externalName || mapping.externalName.length < 1)
                )) {
                    this.validChanges = false;
                }

                if (this.validChanges) {
                    this.profileRequest.preserveFamily = this.preserveFamily;
                    this.profileRequest.loadImages = this.loadImages;
                    if (!this.isXmlFileLoaded && !_.find(this.mappings, (mapping) => mapping.digitalReefMetadataValue === 'extbegdoc')) {
                        this.validChanges = false;
                    }
                }

                if (this.validChanges && this.preserveFamily) {
                    if (!this.isXmlFileLoaded && _.intersection(['extbegattach'], mappedFields).length !== 1) {
                        this.validChanges = false;
                    }
                }
            }
        }
        return this.validChanges;
    }
}
